import { useMemo } from "react";
import { useProjectProductivityQuery } from "../../services/query/project-asset-details.query";
import { isSupportedEnergyProductivityMetric } from "./EnergyProductivity.interface";
import { mapProductivityResponseToSeries } from "./EnergyProductivity.mapper";
/**
 * Resolves the Energy &amp; Productivity chart series for the active selection.
 *
 * Data flow:
 * - The real `GET /projects/{id}/energy/histogram` (aggregation = productivity)
 *   response is fetched via React Query and mapped into the chart series shapes.
 * - The request only fires when a `projectId`, a valid `dateKey` and a supported
 *   `metric` are all present.
 * - Unsupported metrics resolve to `unsupported`; a missing `projectId` or date
 *   resolves to `loading` (no request has run); any load error or empty/absent
 *   response resolves to `error` (surfaced by the panel as the
 *   "No Data Available" message).
 */
export const useEnergyProductivityData = ({ assetFilter, dateKey, metric, projectId }) => {
    const isSupported = isSupportedEnergyProductivityMetric(metric);
    const canFetch = Boolean(projectId && dateKey && isSupported);
    const query = useProjectProductivityQuery({
        enabled: canFetch,
        id: projectId ?? "",
        startDate: dateKey ?? ""
    });
    const { data, isError, isLoading } = query;
    return useMemo(() => {
        if (!isSupported) {
            return { series: null, status: "unsupported" };
        }
        // No project or date selected yet: the query is disabled and no request
        // has run, so treat this as a transient loading state rather than an
        // error (which would misleadingly show "No Data Available").
        if (!projectId || !dateKey) {
            return { series: null, status: "loading" };
        }
        if (isLoading) {
            return { series: null, status: "loading" };
        }
        // React Query can retain stale `data` while `isError` is true; treat any
        // errored request as an error state so backend/auth failures are surfaced
        // as "No Data Available" rather than silently rendering stale data.
        if (data && !isError) {
            return {
                series: mapProductivityResponseToSeries({ assetFilter, dateKey, metric, response: data }),
                status: "success"
            };
        }
        // Query finished without usable data (disabled, load error, or an empty
        // response). Surface the error state so the panel shows "No Data Available".
        return { series: null, status: "error" };
    }, [assetFilter, data, dateKey, isError, isLoading, isSupported, metric, projectId]);
};
